import { EditOutlined } from "@ant-design/icons";
import { Button, Form, Select, Space, Tooltip, message } from "antd";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import {
  customFieldTypeFormatFunctions,
  getCustomFieldsItems,
  getUdfComponent,
} from "common/utils";
import { AktDatePicker } from "components/aktDatePicker";
import { CONSENT_OPTIONS, CONSENT_FIELDS, formatConsent } from "components/consentTag";
import { DisplayComponent } from "components/formItems";
import {
  useFetchDebtorProfileQuery,
  useUpdateDebtorProfileMutation,
} from "features/accountsTable/agencyPortal/accountsTableAPI";
import { useFetchCreditorPortalDebtorProfileQuery } from "features/accountsTable/creditorPortal/accountsTableAPI";
import { useUserType } from "features/auth";
import { useReplicatePacketedDebtorInfoMutation } from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useCompactMode } from "providers/compactModeProvider";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useFetchAllDebtorUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";
import moment from "moment-timezone";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { ConsentDot, ConsentItem, getConsentColor } from "./compactCardUtils";

const StyledCard = styled.div`
  margin-top: 0px;
  margin-left: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledHeaderText = styled.h4`
  margin: 2px 0;
`;

const StyledConsentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const StyledToggleButton = styled(Button)`
  padding: 0;
  font-size: 12px;
  height: auto;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: ${({ $compact }) => ($compact ? "1px" : "4px")};
  }

  ${({ $compact }) =>
    $compact &&
    `
    .ant-form-item-label {
      padding-bottom: 0;
    }
    .ant-form-item-label > label {
      font-size: 12px;
      height: 20px;
    }
    .ant-form-item-control-input {
      min-height: 20px;
    }
    .ant-form-item-control-input-content {
      font-size: 12px;
    }
  `}
`;

export function DebtorInfoCard({
  profileType,
  triggerReplicatePacketedDebtorUpdatesModal,
  alwaysExpanded = false,
}) {
  const { isAgencyUserType } = useUserType();
  const { isCompactMode } = useCompactMode();
  const { debtorId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [showUdfs, setShowUdfs] = useState(false);
  const { data: agencyDebtorPersonalInfo, isSuccess: agencyIsSuccess } = useFetchDebtorProfileQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: debtorUdfCustomFields } = useFetchAllDebtorUdfCustomFieldsQuery();
  const { data: creditorPortalDebtorPersonalInfo, isSuccess: creditorPortalIsSuccess } =
    useFetchCreditorPortalDebtorProfileQuery({ debtorId }, { skip: isAgencyUserType });
  const [debtorPersonalInfo, isSuccess] = isAgencyUserType
    ? [agencyDebtorPersonalInfo, agencyIsSuccess]
    : [creditorPortalDebtorPersonalInfo, creditorPortalIsSuccess];
  const { data: constants } = useFetchBackendConstantsQuery();
  const [replicatePacketedDebtorInfo] = useReplicatePacketedDebtorInfoMutation();

  const {
    data: {
      [PERMISSIONS.DEBTOR__GET_SSN]: canGetFullSSN,
      [PERMISSIONS.DEBTOR__GET_SSN_LAST_4]: canGetSSNLast4,
      [PERMISSIONS.DEBTOR__UPDATE_SSN]: canUpdateSSN,
    },
  } = useAuthorizations();

  const [updateDebtorProfile, { isLoading }] = useUpdateDebtorProfileMutation();

  const [debtorForm] = Form.useForm();
  const formItemLayout = {
    labelCol: {
      xs: { span: 20 },
      sm: { span: 10 },
    },
  };

  const getChangedFields = (newValues, originalValues) => {
    const changed = {};
    Object.entries(newValues).forEach(([key, value]) => {
      if (key === "customFields") {
        const changedCustomFields = {};
        Object.entries(value || {}).forEach(([slug, fieldValue]) => {
          if (JSON.stringify(fieldValue) !== JSON.stringify(originalValues?.customFields?.[slug])) {
            changedCustomFields[slug] = fieldValue;
          }
        });
        if (Object.keys(changedCustomFields).length > 0) {
          changed.customFields = changedCustomFields;
        }
      } else if (JSON.stringify(value) !== JSON.stringify(originalValues?.[key])) {
        changed[key] = value;
      }
    });
    return changed;
  };

  const consentFieldNames = CONSENT_FIELDS.map((f) => f.name);

  const toggleIsEditing = async () => {
    if (isEditing) {
      const values = await debtorForm.validateFields();

      // Only include consent fields in the payload if they were touched
      const payload = { ...values };
      consentFieldNames.forEach((field) => {
        if (!debtorForm.isFieldTouched(field)) {
          delete payload[field];
        } else if (payload[field] === undefined) {
          // User cleared the field - send null to reset to unknown
          payload[field] = null;
        }
      });

      const result = await updateDebtorProfile({
        debtorId,
        ...payload,
      });
      if ("error" in result) {
        debtorForm.setFields(formatError(result.error));
        message.error("Could not update personal information");
        return;
      }

      const changedFields = getChangedFields(payload, debtorPersonalInfo);
      if (debtorPersonalInfo?.packetId && Object.keys(changedFields).length > 0) {
        const newData = [values.firstName, values.middleName, values.lastName]
          .filter(Boolean)
          .join(" ");
        triggerReplicatePacketedDebtorUpdatesModal({
          action: replicatePacketedDebtorInfo,
          changedFields,
          debtorId,
          newData,
          type: "debtor info",
        });
      }

      setIsEditing(false);
    } else {
      setIsEditing(true);
      debtorForm.setFieldsValue(debtorPersonalInfo);
    }
  };

  const onCancel = () => {
    setIsEditing(false);
  };

  const disabledDobDate = (current) => {
    const today = moment().startOf("day").add(1, "day");
    // Can not select today and later.
    return current && current >= today;
  };

  // Compute SSN display value based on permissions
  const displaySSN = useMemo(() => {
    if (!debtorPersonalInfo) return null;

    // If user can see full SSN, show cleaned_ssn (XXX-XX-XXXX format)
    if (canGetFullSSN) {
      return debtorPersonalInfo.cleanedSsn || debtorPersonalInfo.ssn;
    }

    // If user can only see last 4 digits, show obfuscated format
    if (canGetSSNLast4 && debtorPersonalInfo.ssnLast4) {
      return `***-**-${debtorPersonalInfo.ssnLast4}`;
    }

    return null;
  }, [debtorPersonalInfo, canGetFullSSN, canGetSSNLast4]);

  const customFieldsElements = useMemo(
    () =>
      getCustomFieldsItems(debtorPersonalInfo.customFields, debtorUdfCustomFields, {
        includeAll: isEditing,
      }).map(({ field, slug }) => {
        return (
          <DisplayComponent
            key={field.slug}
            isEditing={isEditing}
            slice={debtorPersonalInfo}
            label={field.name}
            name={["customFields", slug]}
            placeholder=""
            outputFormatter={customFieldTypeFormatFunctions[field.type]}
            // There's some weirdness with the boolean type's required prop, fixing later
            required={field.isRequired && field.type !== "boolean"}
          >
            {getUdfComponent(field)}
          </DisplayComponent>
        );
      }),
    [debtorPersonalInfo, debtorUdfCustomFields, isEditing],
  );

  const consentComponents = CONSENT_FIELDS.map((field) => {
    if (!isEditing && isCompactMode) {
      return null;
    }
    return (
      <DisplayComponent
        key={field.name}
        isEditing={isEditing}
        slice={debtorPersonalInfo}
        label={field.label}
        name={field.name}
        showFalsyValues
        outputFormatter={formatConsent}
      >
        <Select options={CONSENT_OPTIONS} />
      </DisplayComponent>
    );
  });

  const debtorComponents = (
    <>
      {!isEditing && isCompactMode && (
        <StyledConsentRow>
          {CONSENT_FIELDS.map((field) => (
            <Tooltip
              key={field.name}
              title={`${field.tagLabel} Consent: ${formatConsent(
                debtorPersonalInfo?.[field.name],
              )}`}
            >
              <ConsentItem>
                <ConsentDot $color={getConsentColor(debtorPersonalInfo?.[field.name] ?? null)} />
                {field.tagLabel}
              </ConsentItem>
            </Tooltip>
          ))}
        </StyledConsentRow>
      )}
      {isEditing && debtorPersonalInfo && (
        <>
          <DisplayComponent
            isEditing={isEditing}
            slice={debtorPersonalInfo}
            label="First Name"
            name="firstName"
            placeholder="Enter First Name..."
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={debtorPersonalInfo}
            label="Middle Name"
            name="middleName"
            placeholder="Enter Middle Name..."
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={debtorPersonalInfo}
            label="Last Name"
            name="lastName"
            placeholder="Enter Last Name..."
          />
        </>
      )}
      {/* Only show SSN field if user has appropriate permissions */}
      {/* View mode: show if user can see full SSN or last 4 digits */}
      {/* Edit mode: show only if user has BOTH full SSN view AND update permissions */}
      {((canGetFullSSN || canGetSSNLast4) && !isEditing) ||
      (canGetFullSSN && canUpdateSSN && isEditing) ? (
        <DisplayComponent
          isEditing={isEditing && canGetFullSSN && canUpdateSSN}
          slice={isEditing ? debtorPersonalInfo : { ssn: displaySSN }}
          label="SSN"
          name="ssn"
          placeholder="123-45-6789"
        />
      ) : null}
      <DisplayComponent
        isEditing={isEditing}
        slice={debtorPersonalInfo}
        label="Date of Birth"
        name="dob"
        isDate
      >
        <AktDatePicker disabledDate={disabledDobDate} />
      </DisplayComponent>
      <DisplayComponent
        isEditing={isEditing}
        slice={debtorPersonalInfo}
        label="Driver's License"
        name="driversLicenseNumber"
        placeholder=""
      />
      <DisplayComponent
        isEditing={isEditing}
        slice={debtorPersonalInfo}
        label="Date of Decease"
        name="deceasedDate"
        isDate
      >
        <AktDatePicker disabledDate={disabledDobDate} />
      </DisplayComponent>
      {consentComponents}
      {isEditing ? (
        <>
          {customFieldsElements}
          <DisplayComponent
            isEditing={isEditing}
            slice={debtorPersonalInfo}
            label="Preferred Language"
            name="preferredLanguage"
            placeholder=""
          >
            <Select
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              options={constants?.languages?.map((language) => ({
                label: language.display,
                value: language.value,
              }))}
            />
          </DisplayComponent>
        </>
      ) : (
        <>
          {(isCompactMode && !alwaysExpanded ? showUdfs : true) && (
            <>
              {customFieldsElements}
              <DisplayComponent
                isEditing={false}
                slice={debtorPersonalInfo}
                label="Preferred Language"
                name="preferredLanguage"
                outputFormatter={(value) =>
                  constants?.languages?.find((l) => l.value === value)?.display ?? value
                }
              />
            </>
          )}
          {isCompactMode && !alwaysExpanded && (
            <StyledToggleButton type="link" onClick={() => setShowUdfs((v) => !v)}>
              {showUdfs ? "See less" : "See more"}
            </StyledToggleButton>
          )}
        </>
      )}
    </>
  );

  const commercialComponents = (
    <>
      <DisplayComponent
        isEditing={isEditing}
        slice={debtorPersonalInfo}
        label="Company"
        name="companyName"
        placeholder="Enter Company Name..."
        rules={[{ required: true }]}
      />
      <DisplayComponent
        isEditing={isEditing}
        slice={debtorPersonalInfo}
        label="EIN"
        name="ein"
        placeholder="Enter EIN..."
      />
      <DisplayComponent
        isEditing={isEditing}
        slice={debtorPersonalInfo}
        label="Default Email"
        name="email"
        placeholder="example@example.com"
      />
      {consentComponents}
      {customFieldsElements}
    </>
  );

  return (
    <StyledCard>
      <StyledHeader>
        <StyledHeaderText>
          {profileType === "commercial" ? "Company Info" : "Personal Info"}
        </StyledHeaderText>
        {!isEditing && isAgencyUserType && (
          <Button
            loading={isLoading}
            type="link"
            icon={<EditOutlined />}
            onClick={toggleIsEditing}
          />
        )}
      </StyledHeader>
      {isSuccess && (
        <StyledForm
          layout="horizontal"
          labelAlign="left"
          {...formItemLayout}
          form={debtorForm}
          $compact={isCompactMode && !isEditing}
        >
          {profileType === "commercial" ? commercialComponents : debtorComponents}
        </StyledForm>
      )}
      {isEditing && (
        <StyledButtonContainer>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button loading={isLoading} onClick={toggleIsEditing} type="primary">
              Save
            </Button>
          </Space>
        </StyledButtonContainer>
      )}
      {debtorPersonalInfo?.payReadyTenant && (
        <>
          <StyledHeader>
            <StyledHeaderText>Pay Ready Info</StyledHeaderText>
          </StyledHeader>
          <StyledForm layout="horizontal" labelAlign="left" {...formItemLayout}>
            <DisplayComponent
              isEditing={false}
              slice={debtorPersonalInfo?.payReadyTenant}
              label="Tenant Type"
              name="tenantType"
            />
            <DisplayComponent
              isEditing={false}
              slice={debtorPersonalInfo?.payReadyTenant}
              label="Resident Status"
              name="residentStatus"
            />
          </StyledForm>
        </>
      )}
    </StyledCard>
  );
}
